<template>
    <div class="relative">
        <template v-if="!showTicketDescription">
            <!-- Show Add issue Form // Hide Add issue Form-->
            <div class="w-full flex justify-center items-center">
                <button v-if="(maintenanceRequest || inspection) && !(form.issues && form.issues.length === 0) && Object.values(issueToEdit).length === 0"
                        title="Click to show or hide form"
                        class="flex items-center text-sm text-gray-400 p-1 hover:text-indigo-600 transition duration-300"
                        @click="showAddIssueForm = !showAddIssueForm">
                    {{ showAddIssueForm ? "Hide Form" : "Create Additional Issue" }}
                    <icon-chevron-down-double class="ml-0.5 h-3 w-3 transform transition duration-300"
                                              :class="showAddIssueForm ? 'rotate-180' : ''"></icon-chevron-down-double>
                </button>
            </div>

            <transition name="slide">
                <div v-show="showAddIssueForm || form.issues && form.issues.length === 0">
                    <div class="font-medium text-md">{{ Object.values(issueToEdit).length > 0 ? "Editing Issue" : "Creating Issue" }}</div>
                    <!-- Create Issues -->
                    <ticket-issue-form
                        ref="ticketIssueForm"
                        :assigned-issue="issueToEdit"
                        @add-new-issue="addNewIssue"
                        @edit-issue="updateIssue"
                        @remove-issue="deleteIssue">
                    </ticket-issue-form>
                </div>
            </transition>

            <!-- Specific edge case for trying to generate task card with no issues -->
            <div class="text-red-500 text-xs break-normal my-2" v-show="form.errors.has('task_settings.generate_pdf')">
                {{ form.errors.get("task_settings.generate_pdf") }}
            </div>

            <!-- Issues list -->
            <transition name="slide">
                <div v-show="form.issues && form.issues.length > 0" class="my-2 z-20">
                    <div class="font-medium text-md">{{ isShowingPhoto ? "Issue Photo" : "Created Issues" }}</div>

                    <ul id="created-issues"
                        class="space-y-2 mt-1 transition duration-300 overscroll-none overflow-y-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100"
                        :class="{'pr-4 md:pr-2' : selectionListIsOverflowing, 'cursor-not-allowed' : (issueToEdit && Object.keys(issueToEdit).length > 0)}"
                        :style="showAddIssueForm ? (notSmallScreen ? 'max-height: 14rem' : 'max-height:10rem') : 'max-height: 23rem'">

                        <li v-for="issue in form.issues" :key="'transition-key-' + issue.issueIndex"
                            class="flex items-center h-auto w-full bg-indigo-50 p-2 rounded-md space-x-2"
                            :class="{'pointer-events-none' : (issueToEdit && Object.keys(issueToEdit).length > 0)}">
                            <!-- Issue information -->
                            <div class="flex flex-col w-full">
                                <!-- DESKTOP STYLING -->
                                <div v-if="notSmallScreen" class="flex justify-between items-center">
                                    <div class="flex items-center space-x-1">
                                        <!-- Show thumbnail or add photo option ticket creation not from inspection -->
                                        <template v-if="!inspection">
                                            <div class="flex">
                                                <div v-for="(file, fileIndex) in issue.files">
                                                    <div class="h-5 w-5 max-w-5 max-h-5 ml-1">
                                                        <!-- Thumbnail that expands photo -->
                                                        <button class="outline-none h-5 w-5 max-w-5 max-h-5 rounded-sm"
                                                                @click="toggleCreateIssuePhoto(fileIndex, issue)">
                                                            <img v-if="file.url_thumb_md" class="h-full w-full object-fill rounded-sm" :src="file.url_thumb_md" alt=""/>
                                                            <img v-else class="h-full w-full object-fill rounded-sm" :src="getFileSource(file)" alt=""/>
                                                        </button>

                                                        <!-- Image to show after clicking on thumbnail -->
                                                        <div :id="'issue-' + issue.issueIndex + '-photo-' + fileIndex"
                                                             class="z-1000 absolute md:top-44 md:right-0 hidden rounded-md h-48 w-64 md:h-full md:max-h-72 md:w-full">
                                                            <img v-if="file.url" class="h-full w-full object-fill rounded-md" @click="closeAllIssuePhotos"
                                                                 :src="file.url" alt=""/>
                                                            <img v-else class="h-full w-full object-fill rounded-md" @click="closeAllIssuePhotos"
                                                                 :src="getFileSource(file)" alt=""/>

                                                            <button @click="removePhotoFromIssueList(fileIndex, issue)"
                                                                    class="absolute bottom-0 m-2 left-0 bg-gray-100 text-brand hover:text-indigo-600 rounded-full bg-opacity-80 outline-none p-1">
                                                                <icon-trash class="w-6 h-6"></icon-trash>
                                                            </button>
                                                            <button @click="closeAllIssuePhotos"
                                                                    class="absolute top-0 right-0 m-2 bg-gray-100 bg-opacity-80 hover:text-indigo-600 rounded-full outline-none p-1">
                                                                <icon-x-circle
                                                                    class="w-4 h-4 text-gray-800 hover:text-indigo-600 transition duration-300"></icon-x-circle>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Button and Camera-Plus Icon to add photos -->
                                                <div v-for="addPhotoIndex in (3 - issue.files.length)"
                                                     :key="addPhotoIndex"
                                                     class="text-indigo-600 hover:text-indigo-800 cursor-pointer outline-none h-5 w-5 ml-1"
                                                     @click="handleAddPhotoClick(addPhotoIndex, issue.issueIndex)">
                                                    <!-- If we are uploading replace the very first icon with a loading icon -->
                                                    <template v-if="isProcessingS3[issue.issueIndex] && addPhotoIndex === 1">
                                                        <icon-refresh class="h-5 w-5 text-indigo-600 animate-spin"/>
                                                    </template>
                                                    <template v-else>
                                                        <icon-camera-plus class="h-5 w-5"
                                                                          :class="isProcessingS3[issue.issueIndex] ? 'text-gray-600 cursor-not-allowed' : ''">
                                                        </icon-camera-plus>
                                                        <input :ref="'fileInput-' + issue.issueIndex + '-' + addPhotoIndex"
                                                               :name="issue.issueIndex"
                                                               :disabled="isProcessingS3[issue.issueIndex]"
                                                               @change="onFileChanged" type="file" class="hidden"
                                                               accept=".jpg, .jpeg, .png"/>
                                                    </template>
                                                </div>
                                            </div>
                                        </template>

                                        <template v-else>
                                            <!-- Show Issue Photo Preview For Inspection Checklist issue photos-->
                                            <button @click="toggleLightboxIssuePhoto(issue)" class="outline-none" title="View Issue Photos">
                                                <icon-photograph class="h-5 w-5"
                                                                 :class="issue.photo ? 'text-indigo-600' :'text-indigo-300 cursor-default'"></icon-photograph>
                                            </button>
                                        </template>

                                        <span class="font-medium" v-if="issue.location">{{ issue.location }}</span>
                                        <span class="font-normal">{{ issue.item }}</span>
                                    </div>

                                    <div class="flex items-center space-x-3">
                                        <span class="text-indigo-600 font-medium bg-white text-xs px-2 py-0.5 rounded-md"> {{ issue.required_action }}</span>
                                        <button @click.prevent="changeTicketIssueFormMode(issue)" title="Edit Issue"
                                                class="outline-none flex justify-center">
                                            <icon-pencil-alt class="w-4 h-4 text-gray-400 hover:text-indigo-600 transition duration-300"></icon-pencil-alt>
                                        </button>

                                        <button class="outline-none font-bold" title="Remove Issue"
                                                :disabled="isProcessingS3[issue.issueIndex]"
                                                @click.prevent="deleteIssue(issue.issueIndex)">
                                            <icon-x-circle class="w-4 h-4 text-gray-400 hover:text-brand transition duration-300"></icon-x-circle>
                                        </button>
                                    </div>
                                </div>

                                <!-- MOBILE STYLING -->
                                <div v-if="!notSmallScreen">
                                    <div class="flex flex-col space-y-2">
                                        <!-- Show thumbnail or add photo option ticket creation not from inspection -->
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center">
                                                <template v-if="!inspection">
                                                    <div v-for="(file, fileIndex) in issue.files">
                                                        <div class="h-5 w-5 max-w-5 max-h-5 ml-1">
                                                            <!-- Thumbnail that expands photo -->
                                                            <button class="outline-none h-5 w-5 max-w-5 max-h-5 rounded-sm"
                                                                    @click="toggleCreateIssuePhoto(fileIndex, issue)">
                                                                <img v-if="file.url_thumb_md" class="h-full w-full object-fill rounded-sm" :src="file.url_thumb_md"
                                                                     alt=""/>
                                                                <img v-else class="h-full w-full object-fill rounded-sm" :src="getFileSource(file)" alt=""/>
                                                            </button>

                                                            <!-- Image to show after clicking on thumbnail -->
                                                            <div :id="'issue-' + issue.issueIndex + '-photo-' + fileIndex"
                                                                 class="z-1000 absolute md:top-44 md:right-0 hidden rounded-md h-48 w-64 md:h-full md:max-h-72 md:w-full">
                                                                <img v-if="file.url" class="h-full w-full object-fill rounded-md" @click="closeAllIssuePhotos"
                                                                     :src="file.url" alt=""/>
                                                                <img v-else class="h-full w-full object-fill rounded-md" @click="closeAllIssuePhotos"
                                                                     :src="getFileSource(file)" alt=""/>

                                                                <button @click="removePhotoFromIssueList(fileIndex, issue)"
                                                                        class="absolute bottom-0 m-2 left-0 bg-gray-100 text-brand hover:text-indigo-600 rounded-full bg-opacity-80 outline-none p-1">
                                                                    <icon-trash class="w-4 h-4"></icon-trash>
                                                                </button>
                                                                <button @click="closeAllIssuePhotos"
                                                                        class="absolute top-0 right-0 m-2 bg-gray-100 bg-opacity-80 hover:text-indigo-600 rounded-full outline-none p-1">
                                                                    <icon-x-circle
                                                                        class="w-4 h-4 text-gray-800 hover:text-indigo-600 transition duration-300"></icon-x-circle>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Button and Camera-Plus Icon to add photos -->
                                                    <div v-for="addPhotoIndex in  (3 - issue.files.length)"
                                                         class="text-indigo-600 hover:text-indigo-800 cursor-pointer outline-none h-5 w-5 ml-1"
                                                         @click="handleAddPhotoClick(addPhotoIndex, issue.issueIndex)">
                                                        <!-- If we are uploading replace the very first icon with a loading icon -->
                                                        <template v-if="isProcessingS3[issue.issueIndex] && addPhotoIndex === 1">
                                                            <icon-refresh class="h-5 w-5 text-indigo-600 animate-spin"/>
                                                        </template>
                                                        <template v-else>
                                                            <icon-camera-plus class="h-5 w-5"
                                                                              :class="isProcessingS3[issue.issueIndex] ? 'text-gray-600 cursor-not-allowed' : ''">
                                                            </icon-camera-plus>
                                                            <input :ref="'fileInput-' + issue.issueIndex + '-' + addPhotoIndex"
                                                                   :name="issue.issueIndex"
                                                                   :disabled="isProcessingS3[issue.issueIndex]"
                                                                   @change="onFileChanged" type="file" class="hidden"
                                                                   accept=".jpg, .jpeg, .png"/>
                                                        </template>
                                                    </div>
                                                </template>

                                                <template v-else>
                                                    <!-- Show Issue Photo Preview For Inspection Checklist issue photos-->
                                                    <button @click="toggleLightboxIssuePhoto(issue)" class="outline-none" title="View Issue Photos">
                                                        <icon-photograph class="h-5 w-5"
                                                                         :class="issue.photo ? 'text-indigo-600' :'text-indigo-300 cursor-default'"></icon-photograph>
                                                    </button>
                                                </template>
                                            </div>

                                            <div class="flex items-center gap-x-2">
                                                <button @click.prevent="changeTicketIssueFormMode(issue)" title="Edit Issue"
                                                        class="outline-none flex justify-center">
                                                    <icon-pencil-alt
                                                        class="w-4 h-4 text-gray-400 hover:text-indigo-600 transition duration-300"></icon-pencil-alt>
                                                </button>

                                                <button class="outline-none font-bold" title="Remove Issue"
                                                        :disabled="isProcessingS3[issue.issueIndex]" @click.prevent="deleteIssue(issue.issueIndex)">
                                                    <icon-x-circle class="w-4 h-4 text-gray-400 hover:text-brand transition duration-300"></icon-x-circle>
                                                </button>
                                            </div>
                                        </div>

                                        <div class="flex flex-col space-y-1 ml-1">
                                            <div class="flex items-center space-x-1">
                                                <span class="font-medium" v-if="issue.location">{{ issue.location }}</span>
                                                <span class="font-normal">{{ issue.item }}</span>
                                            </div>
                                            <span class="text-indigo-600 font-medium text-sm"> {{ issue.required_action }}</span>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="issue.description" class="pr-2 text-sm ml-1 mt-1">{{ issue.description }}</div>
                            </div>
                        </li>
                    </ul>
                </div>
            </transition>
        </template>

        <ticket-auto-description ref="ticketAutoDescription" :show="showTicketDescription" :form="form" :is-showing-photo="isShowingPhoto" :inspection="inspection"
                                 :issue-to-edit="issueToEdit" :gotFocus="gotFocus"
                                 @close="showTicketDescription = false; $emit('disableNextButton', false)"
                                 @open="showTicketDescription = true; $emit('disableNextButton', true)">
        </ticket-auto-description>

        <lightbox :images="lightboxImageUrls" :show-lightbox="showLightbox" @close="showLightbox = false" :width="400" :height="400"></lightbox>
    </div>
</template>

<script>
import IssueMixin from "../../../mixins/issues-mixin";
import UuidMixin from "../../../mixins/uuid-mixin";

export default {
    name: "TicketCreateModalCreateIssues",

    mixins: [IssueMixin, UuidMixin],

    props: {
        form: {
            required: true,
            type: Object,
        },
        inspection: {
            required: false,
            type: Object,
            default: null,
            description: "Used when creating a ticket from an Inspection"
        },
        maintenanceRequest: {
            required: false,
            type: Object,
            default: null,
            description: "Used when creating a ticket from an Maintenance Request"
        },
        gotFocus: {
            required: false,
            type: Boolean,
            default: false,
            description: "Used when previous then next buttons was clicked to trigger on focus events"
        }
    },

    data () {
        return {
            isProcessingS3: {},
            isShowingPhoto: false,
            selectionListIsOverflowing: false,
            issueToEdit: {},
            showLightbox: false,
            lightboxImageUrls: [],
            showTicketDescription: false,
            showAddIssueForm: true,
        };
    },

    watch: {
        "form.issues": function () {
            this.determineIssueListOverflow();

            if (this.form.ticket_details.auto_generated_description) {
                this.form.ticket_details.description = this.$refs.ticketAutoDescription.setTicketToAutoDescription();
            }

            if (this.form.errors.has("task_settings.generate_pdf")) {
                this.form.errors.forget("task_settings.generate_pdf");
            }
        },

        showAddIssueForm () {
            this.determineIssueListOverflow();
        },

        // Watcher because not triggered in mounted
        maintenanceRequest (newVal) {
            if (newVal) {
                this.showAddIssueForm = false;
            }
        }
    },

    mounted () {
        if (this.inspection) {
            this.showAddIssueForm = false;
        }
    },

    methods: {
        changeTicketIssueFormMode (issue) {
            this.issueToEdit = issue;
            this.showAddIssueForm = true;
            this.showTicketDescription = false;
        },

        autoFinalizeIssueWhenClickingNext () {
            this.$refs.ticketIssueForm.createNewIssueItem(true);
        },

        addNewIssue (issue) {
            issue.issueIndex = this.form.issues.length;
            issue.files = [];
            this.form.issues.push(issue);
            this.form.issues = _.sortBy(this.form.issues, "required_action");
            this.recalculateIssueIndexes();
        },

        updateIssue (issueIndex, issue) {
            this.issueToEdit = {};
            this.form.issues[issueIndex] = issue;
            if (this.form.ticket_details.auto_generated_description) {
                this.form.ticket_details.description = this.$refs.ticketAutoDescription.setTicketToAutoDescription();
            }
        },

        deleteIssue (issueIndex) {
            this.issueToEdit = {};
            this.form.issues.splice(issueIndex, 1);
            this.recalculateIssueIndexes();
        },

        recalculateIssueIndexes () {
            this.form.issues.forEach((issue, index) => {
                issue.issueIndex = index;
            });
        },

        toggleCreateIssuePhoto (index, selectedIssue) {
            this.showTicketDescription = false;
            let self = this;

            // Close other photos and only open/close the one clicked on
            this.form.issues.forEach(function (issue) {
                let i;
                for (i = 0; i <= 2; i++) {
                    let image = document.getElementById("issue-" + issue.issueIndex + "-photo-" + i);
                    if (image) {
                        if (image.classList.contains("hidden") && selectedIssue.issueIndex === issue.issueIndex && index === i) {
                            image.classList.remove("hidden");
                            self.isShowingPhoto = true;
                        } else {
                            image.classList.add("hidden");
                        }
                    }
                }
            });
        },

        toggleLightboxIssuePhoto (issue) {
            this.lightboxImageUrls = [];

            if (!issue.photo) {
                this.showLightbox = false;
                return;
            }

            axios.get(`/inspections/items/${issue.checklist_item_id}/photos`)
            .then(response => {
                this.showLightbox = !this.showLightbox;
                let media = response.data;
                this.lightboxImageUrls = media.map(media => media.url);
            });
        },

        closeAllIssuePhotos () {
            this.form.issues.forEach(function (issue) {
                let i;
                for (i = 0; i <= 2; i++) {
                    let image = document.getElementById("issue-" + issue.issueIndex + "-photo-" + i);
                    if (image) {
                        image.classList.add("hidden");
                    }
                }
            });
            this.isShowingPhoto = false;
        },

        removePhotoFromIssueList (fileIndex, issue) {
            issue.files.splice(fileIndex, 1);
            if ((issue.files && issue.files.length === 0) || fileIndex > 0) {
                this.closeAllIssuePhotos();
            }
            this.$forceUpdate();
        },

        handleAddPhotoClick (fileIndex, issueIndex) {
            this.closeAllIssuePhotos();
            // Only allow uploads when not uploading
            if (_.isEmpty(this.isProcessingS3)) {
                let fileInputConcat = "fileInput-" + issueIndex + "-" + fileIndex;
                this.$refs[fileInputConcat][0].click();
            }
        },

        async onFileChanged (event) {
            const file = event.target.files?.[0];
            const isImg = ["image/jpeg", "image/png"].includes(_.toLower(file.type));

            if (!isImg) {
                toastr.error("Only jpg en png photos allowed!");
                return;
            }
            const MAX_SIZE = 3 * 1024 * 1024; // 3MB in bytes (for better readability)

            if (file.size > MAX_SIZE) {
                toastr.error("File size cannot exceed 3Mb!");
                return;
            }

            let issue = this.form.issues[event.target.name];
            let allFiles = issue.files ? issue.files : [];

            // Don't add the same file
            if ((allFiles.filter(existingFile => existingFile.name === file.name && existingFile.size === file.size)).length > 0) {
                return;
            }

            if (!this.hasFeature("release_ticket_backend_rework")) {
                // Get issue and its current files
                if (allFiles.length < 3) {
                    allFiles.push(event.target.files[0]);
                }

                this.$set(issue, "files", allFiles);
                this.$forceUpdate();
            } else {
                this.$set(this.isProcessingS3, event.target.name, true);
                try {
                    const uploadedFileData = await this.generateSignedUrlForFile(file);

                    if (allFiles.length < 3) {
                        allFiles.push(uploadedFileData);
                    }

                    this.$set(issue, "files", allFiles);
                    this.isProcessingS3 = {};
                } catch (error) {
                    this.isProcessingS3 = {};
                    toastr.error("Failed to upload photo. Please try again.");
                }
            }
        },

        async generateSignedUrlForFile (file) {
            try {
                const uuid = this.generateUUID();
                const signUrlResponse = await App.post("/generate-s3-url", new AppForm({
                    uuid: uuid,
                    name: file.name
                }));

                await this.uploadToS3(file, signUrlResponse);

                return {
                    uuid,
                    name: file.name,
                    size: file.size,
                    path: signUrlResponse.signature,
                    url: this.getFileSource(file),
                    url_thumb_md: this.getFileSource(file),
                };
            } catch (err) {
                throw err;
            }
        },

        async uploadToS3 (file, {postEndpoint, signature}) {
            const formData = new FormData();
            for (const key in signature) {
                formData.append(key, signature[key]);
            }
            formData.append("file", file);

            const response = await axios.post(postEndpoint, formData, {
                headers: {"Content-Type": "multipart/form-data"}
            });

            if (response.status !== 201) {
                throw new Error(`File upload failed: ${response.statusText}`);
            }

            return response;
        },

        getFileSource (file) {
            return window.URL.createObjectURL(file);
        },

        determineIssueListOverflow () {
            let content = document.getElementById("created-issues");
            // Give the dom .05 seconds to update the content's scrollHeight
            if (content) {
                let self = this;
                setTimeout(function () {
                    self.selectionListIsOverflowing = content.scrollHeight > content.clientHeight;
                }, 20);
            }
        }
    },
};
</script>